import React, { useState } from 'react';
import { Modal, Form, Input, Button, Space, Typography } from 'antd';
import { SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { generatePDFFromRef } from '../../utils/generatePDF';
import sendPatientMessage from '../../utils/patient/sendPatientMessage';
import { logEvent } from '../../analytics';

const { TextArea } = Input;
const { Text } = Typography;

const SendPatientInstructionsModal = ({ open, onClose, noteContentRef, source, patient, onInstructionsSent }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSending, setIsSending] = useState(false);

  // Use the passed patient prop if available, otherwise fall back to source.getPatient()
  const currentPatient = patient || source?.getPatient();
  const patientEmail = currentPatient?.getEmail();

  const handleSendEmail = async (values) => {
    if (!currentPatient) {
      toast.error(t('send_patient_instructions.errors.no_patient'));
      return;
    }

    try {
      setIsSending(true);

      // Generate PDF using the shared utility
      const now = new Date();
      const formattedDate = now.toISOString().slice(0, 10);
      const formattedTime = now.toTimeString().slice(0, 5).replace(':', '-');
      const filename = `patient_instructions_${formattedDate}_${formattedTime}.pdf`;
      
      const pdfBlob = await generatePDFFromRef(noteContentRef, {
        filename,
        method: 'blob', // Return blob instead of saving
        resolution: 3
      });
      
      // If generatePDF doesn't return a blob, we might need to handle this differently
      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        console.error('generatePDF did not return a blob:', pdfBlob);
        throw new Error('Failed to generate PDF blob');
      }
      
      // Send email with PDF attachment and custom message
      await sendPatientMessage({
        uuid: currentPatient.getUUID(),
        subject: t('send_patient_instructions.subject'),
        body: values.message,
        attachment: pdfBlob,
        filename,
        sessionUuid: source.getUUID()
      });
      
      // Log analytics event after successful send
      logEvent('Note', 'SendPatientInstructions');
      
      // Notify parent component that instructions were sent
      if (onInstructionsSent) {
        onInstructionsSent();
      }
      
      toast.success(t('send_patient_instructions.success'));
      onClose();
    } catch (error) {
      console.error('Error sending patient instructions:', error);
      toast.error(t('send_patient_instructions.errors.general'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      title={t('send_patient_instructions.modal.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
    >
      <div style={{ marginTop: '20px' }}>
        <Form
          form={form}
          onFinish={handleSendEmail}
          layout="vertical"
          initialValues={{
            message: t('send_patient_instructions.default_message')
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
              {t('send_patient_instructions.modal.to_label')}
            </Text>
            <Text style={{ fontSize: '14px' }}>
              {patientEmail}
            </Text>
          </div>

          <Form.Item
            name="message"
            label={<Text type="secondary">{t('send_patient_instructions.modal.message_label')}:</Text>}
          >
            <TextArea
              rows={4}
              placeholder={t('send_patient_instructions.modal.message_placeholder')}
              style={{ fontSize: '14px' }}
            />
          </Form.Item>

          <div style={{ marginBottom: '20px' }}>
            <Space align="start">
              <PaperClipOutlined style={{ color: '#1890ff', marginTop: '4px' }} />
              <div>
                <Text>{t('send_patient_instructions.modal.pdf_attached')}</Text>
                <br />
                <Text type="secondary">{t('send_patient_instructions.modal.pdf_description')}</Text>
              </div>
            </Space>
          </div>

          <Form.Item style={{ marginBottom: 10, textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                htmlType="submit"
                loading={isSending}
              >
                {t('send_patient_instructions.modal.send_button')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default SendPatientInstructionsModal; 